import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
} from '@mui/material';
import withWebsiteContent from '../../../common/withWebsiteContent';

const isExternalUrl = (url) =>
  typeof url === 'string' &&
  (/^https?:\/\//i.test(url) || url.startsWith('//'));

/**
 * Resolves a feature's image key to a full URL by matching the filename stem
 * (without extension) against the URL paths in the images array.
 *
 * e.g. image key "6072be10" matches ".../images/6072be10.png"
 */
const resolveImageUrl = (imageKey, images) => {
  if (!imageKey || !Array.isArray(images) || images.length === 0) return null;
  for (const url of images) {
    const stem = url.split('/').pop()?.replace(/\.[^.]+$/, '') ?? '';
    if (stem === imageKey) return url;
  }
  return null;
};

/**
 * Pure Features component — all content supplied via props.
 *
 * Expected website-component defaultProps shape (features.json):
 * {
 *   "title":    "...",
 *   "subtitle": "...",
 *   "features": [
 *     { "id": "...", "name": "...", "description": "...", "image": "<filename-stem>" }
 *   ],
 *   "buttonLabel": "More info",  // optional; both label + URL required to show button
 *   "buttonUrl":   "/about"
 * }
 *
 * The `images` prop (injected by withWebsiteContent) contains the full URLs of
 * images stored in the component's images/ folder in MinIO. Each feature's
 * `image` field is matched against those URLs by filename stem to resolve imageUrl.
 */
export const FeaturesPure = ({
  title = '',
  subtitle = '',
  features = [],
  images = [],
  buttonLabel,
  buttonUrl,
}) => {
  const resolvedFeatures = features.map((f) => ({
    ...f,
    imageUrl: resolveImageUrl(f.image, images),
  }));

  const hasActionButton =
    typeof buttonLabel === 'string' &&
    buttonLabel.trim() !== '' &&
    typeof buttonUrl === 'string' &&
    buttonUrl.trim() !== '';

  if (resolvedFeatures.length === 0) return null;

  return (
    <Container sx={{ py: 8 }}>
      {title && (
        <Typography
          variant="h4"
          component="h2"
          sx={{ textAlign: 'center', mb: subtitle ? 2 : 6, fontWeight: 600 }}
        >
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography
          variant="h6"
          component="p"
          color="text.secondary"
          sx={{ textAlign: 'justify', mb: 6 }}
        >
          {subtitle}
        </Typography>
      )}
      <Grid container spacing={4}>
        {resolvedFeatures.map((feature, index) => (
          <Grid item xs={12} sm={6} md={4} key={feature.id || index}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardMedia
                component={feature.imageUrl ? 'img' : 'div'}
                image={feature.imageUrl || undefined}
                alt={feature.name || ''}
                sx={{
                  height: 200,
                  backgroundColor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...(feature.imageUrl && { objectFit: 'cover' }),
                }}
              />
              <CardContent>
                {feature.name && (
                  <Typography variant="h6" component="h3" gutterBottom>
                    {feature.name}
                  </Typography>
                )}
                {feature.description && (
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {hasActionButton && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          {isExternalUrl(buttonUrl) ? (
            <Button
              component="a"
              href={buttonUrl}
              variant="contained"
              size="large"
              rel="noopener noreferrer"
              target="_blank"
            >
              {buttonLabel}
            </Button>
          ) : (
            <Button
              component={Link}
              to={buttonUrl}
              variant="contained"
              size="large"
            >
              {buttonLabel}
            </Button>
          )}
        </Box>
      )}
    </Container>
  );
};

const Features = withWebsiteContent(FeaturesPure);
Features.displayName = 'Features';

export default Features;
